// Home read-model (DC61; customer gap §A.3 H1–H6, Home.dc.html script L672-878). Pure
// over contract data — the screen arranges what the server returns, it never prices,
// ranks for money or writes a status. The design answers two questions only: what is
// next, and is anything waiting on me.
//   - hero     = the soonest live booking (the design's `live[0]`);
//   - next up  = the two live bookings after it;
//   - attention = rows the customer can act on, urgency first: a rejected proof
//                (danger, "Resubmit proof") before a completed visit to rate (ok).
//                The design's third kind — a waitlist slot that opened — is not built:
//                the waitlist is deferred (DR-42) and the contract has no entity for it.
import { calendarDayDiff } from '@bookingdei/i18n/format-date';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { displayStatus, inTab } from '../bookings/booking-tabs';
import { isProofRejected } from '../bookings/booking-payment';
import type { Booking, OfferCard, Review } from '../../mock/seeds/schema-types';
import { isBookable } from '../new-booking/is-bookable';

/** Live bookings from today on, soonest first — the hero and the next-up list. */
export function liveBookings(bookings: readonly Booking[], now: Date): Booking[] {
  return bookings
    .filter((b) => inTab('upcoming', b.status))
    .filter((b) => !b.starts_at || calendarDayDiff(new Date(b.starts_at), now) >= 0)
    .sort((a, b) => (a.starts_at ?? '').localeCompare(b.starts_at ?? ''));
}

/** "N bookings today" — every booking that starts today (the design counts all). */
export function todayCount(bookings: readonly Booking[], now: Date): number {
  return bookings.filter((b) => b.starts_at && calendarDayDiff(new Date(b.starts_at), now) === 0)
    .length;
}

export type HeroState = 'rejected' | 'running' | 'payment_verification' | 'pending' | 'confirmed';

/** Which status line the hero strip reads (design `H.heroStatus`). */
export function heroState(b: Booking, now: Date): HeroState {
  if (isProofRejected(b)) return 'rejected';
  const shown = displayStatus(b, now);
  if (shown === 'running') return 'running';
  if (shown === 'payment_verification' || shown === 'pending') return shown;
  return 'confirmed';
}

export const HERO_LINE: Record<HeroState, MessageKey> = {
  rejected: 'user.home.hero.rejected',
  running: 'user.home.hero.running',
  payment_verification: 'user.home.hero.payment_verification',
  pending: 'user.home.hero.pending',
  confirmed: 'user.home.hero.confirmed',
};

/** The scan bar runs while the customer is waiting on someone else (design `heroBusy`). */
export function heroBusy(state: HeroState): boolean {
  return state === 'payment_verification' || state === 'pending' || state === 'running';
}

export type AttentionRow =
  { kind: 'proof'; booking: Booking } | { kind: 'rate'; booking: Booking; yesterday: boolean };

/** Rows that need the customer, urgency first (design ranks proof 0 · waitlist 1 · rate 2). */
export function attentionRows(
  bookings: readonly Booking[],
  reviews: readonly Review[],
  now: Date,
): AttentionRow[] {
  const reviewed = new Set(reviews.map((r) => r.booking_id).filter(Boolean));
  const proof: AttentionRow[] = bookings
    .filter((b) => inTab('upcoming', b.status) && isProofRejected(b))
    .sort((a, b) => (a.starts_at ?? '').localeCompare(b.starts_at ?? ''))
    .map((booking) => ({ kind: 'proof', booking }));
  const rate: AttentionRow[] = bookings
    .filter((b) => b.status === 'completed' && !reviewed.has(b.id))
    .sort((a, b) => (b.starts_at ?? '').localeCompare(a.starts_at ?? ''))
    .map((booking) => ({
      kind: 'rate',
      booking,
      yesterday: !!booking.starts_at && calendarDayDiff(new Date(booking.starts_at), now) === -1,
    }));
  return [...proof, ...rate];
}

/** At most three rows on Home; the rest behind "N more →" (design attShown / attHidden). */
export const ATTENTION_SHOWN = 3;

/** The marquee holds at most ten offers (design `BD.OFFERS.slice(0, 10)`). */
export const OFFERS_SHOWN = 10;

/** Percent off for the card's big figure; a fixed discount shows its effective percent. */
export function offerPercent(o: OfferCard): number {
  if (o.discount_kind === 'percent') return o.discount_value;
  return o.price_before_minor > 0
    ? Math.round(((o.price_before_minor - o.price_after_minor) / o.price_before_minor) * 100)
    : 0;
}

/**
 * The countdown chip, shown only inside three days (design: `endsInHrs < 72`). Expiry
 * is day-granular on the wire (FR-C19 `ends_in_days`, "not a ticking clock"), so the
 * design's hour countdown ("20h left") reads "Ends today" here.
 */
export function offerEnds(o: OfferCard): { days: number } | null {
  const days = o.ends_in_days;
  if (days === undefined || days === null) return null;
  return days < 3 ? { days } : null;
}

/**
 * Where an offer card goes: the in-app wizard at the offer's service when its listing
 * takes online bookings (the card's `bookable` and the listing's DR-77 `bookable`);
 * otherwise the website business page, which renders contact-only when booking is off.
 */
export function offerTarget(o: OfferCard, websiteOrigin: string): { href: string; internal: boolean } {
  if (o.catalog_item_slug && o.bookable !== false && isBookable(o.business)) {
    return {
      href: `/bookings/new?business=${encodeURIComponent(o.business.slug)}&service=${encodeURIComponent(o.catalog_item_slug)}&offer=${encodeURIComponent(o.id)}`,
      internal: true,
    };
  }
  return { href: `${websiteOrigin}/b/${encodeURIComponent(o.business.slug)}`, internal: false };
}
